import React, { useEffect, useMemo, useState } from "react";
import { useDeal } from "../lib/store";
import { useAuth } from "../lib/auth";
import { dealsApi, teamApi } from "../lib/api";
import * as m from "../lib/model";
import { ArrowRight, Lock, Search, LayoutGrid, Rows3, Building2, PiggyBank, KeyRound } from "lucide-react";
import { grantsFor } from "../lib/access";
import { Avatar } from "../components/team/TeamChips";

const matches = (g, d) => (g.dealKey && g.dealKey === (d.model || {}).dealKey) || (g.dealName && g.dealName === d.name);

function DealCard({ d, g, pages, team, current, onOpen, view }) {
  const dm = d.model || {};
  const all = g.some((x) => x.all);
  const tabs = all ? [] : [...new Set(g.flatMap((x) => x.tabs || []))];
  const roles = [...new Set(g.flatMap((x) => x.roles || []))];
  const scopes = [...new Set(g.flatMap((x) => x.scopes || []))];
  const v = dm.sde ? m.verdict(dm) : null;
  const wf = dm.sde ? m.lpWaterfall(dm) : null;
  const dscr = dm.sde ? m.dscrYr1(dm) : 0;
  const label = (id) => pages.find((p) => p.id === id)?.label || id;
  return (
    <div className={`md-card ${view} ${current ? "current" : ""}`} data-testid={`mydeal-${d.id}`}>
      <div className="md-strip">
        <span className={`pulse-dot ${v === "GO" ? "go" : v === "PASS" ? "pass" : "amber"}`} />
        <div className="md-name"><b>{d.name}</b><small>{[dm.industry, dm.address].filter(Boolean).join(" · ") || "—"}</small></div>
        <span className="tag">{d.stage}</span>
        {current && <span className="tag md-cur">Open now</span>}
      </div>
      <div className="md-kpis">
        <div><span>EBITDA</span><b>{m.fmt$(dm.sde || 0)}</b></div>
        <div><span>{m.isRecap(dm) ? "Recap EV" : "Price"}</span><b>{m.fmt$(dm.askPrice || 0)}</b></div>
        <div><span>Multiple</span><b>{dm.sde ? m.fmtX((dm.askPrice || 0) / dm.sde) : "—"}</b></div>
        <div><span>LP raise</span><b>{m.fmt$(dm.sde ? m.lpEquityBase(dm) : 0)}</b></div>
        <div><span>LP MOIC</span><b className={wf ? (m.moicTone(wf.lpMoic) === "go" ? "pos" : "") : ""}>{wf ? m.fmtX(wf.lpMoic) : "—"}</b></div>
        <div><span>DSCR Y1</span><b className={dscr ? (m.dscrTone(dscr) === "go" ? "pos" : m.dscrTone(dscr) === "pass" ? "neg" : "") : ""}>{dscr ? m.fmtX(dscr) : "—"}</b></div>
      </div>
      <div className="md-meta">
        <div className="md-meta-col">
          <span className="md-lbl"><KeyRound size={10} /> Your access</span>
          <div className="md-tabs">{all ? <span className="tag lime">Entire deal</span> : tabs.map((t) => <span key={t} className="tag">{label(t)}</span>)}</div>
        </div>
        <div className="md-meta-col">
          <span className="md-lbl">{scopes.includes("raise") ? <PiggyBank size={10} /> : <Building2 size={10} />} {scopes.map((s) => (s === "raise" ? "Fundraising team" : "Deal team")).join(" · ") || "Team"}{roles.length ? ` · ${roles.join(", ")}` : ""}</span>
          <div className="tm-stack">
            {team.length ? team.map((a) => <Avatar key={a.id} name={a.name} email={a.email} size={22} title={`${a.name}${a.role ? ` · ${a.role}` : ""}`} />) : <span className="tm-none">Just you so far</span>}
          </div>
        </div>
        <button className="btn btn-primary md-open" onClick={() => onOpen(dm, d, all ? null : tabs)} data-testid={`mydeal-open-${d.id}`}>Open deal <ArrowRight size={12} /></button>
      </div>
    </div>
  );
}

export default function MyDeals({ pages, onOpen }) {
  const { M, loadModel, dealName } = useDeal();
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [teams, setTeams] = useState({});
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("grid");
  const grants = user?.access?.deals || [];

  useEffect(() => {
    dealsApi.list().then(async (ds) => {
      setDeals(ds);
      const t = await Promise.all(ds.map((d) => teamApi.assignments({ dealKey: (d.model || {}).dealKey || "", dealName: d.name }).catch(() => [])));
      setTeams(Object.fromEntries(ds.map((d, i) => [d.id, t[i]])));
    }).catch(() => setDeals([])).finally(() => setBusy(false));
  }, []);

  const rows = useMemo(() => deals.filter((d) => !q || `${d.name} ${(d.model || {}).industry || ""} ${(d.model || {}).address || ""}`.toLowerCase().includes(q.toLowerCase())), [deals, q]);
  const raiseCount = grants.filter((g) => (g.scopes || []).includes("raise")).length;
  const open = (dm, d, tabs) => { loadModel(dm, d.id, d.name, d.market); onOpen(tabs); };

  return (
    <div className="page" data-testid="page-mydeals">
      <div className="page-head cm-head">
        <div>
          <div className="page-title">My Deals</div>
          <div className="page-sub">Welcome back, {user?.name || user?.email}. These are the deals shared with you — open one to work in the sections you've been given.</div>
        </div>
        <div className="md-tools">
          <div className="md-search"><Search size={13} /><input className="inp" placeholder="Search deals…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mydeals-search" /></div>
          <div className="md-view">
            <button className={`btn btn-sm ${view === "grid" ? "on" : ""}`} onClick={() => setView("grid")} title="Grid" data-testid="mydeals-grid"><LayoutGrid size={13} /></button>
            <button className={`btn btn-sm ${view === "list" ? "on" : ""}`} onClick={() => setView("list")} title="List" data-testid="mydeals-list"><Rows3 size={13} /></button>
          </div>
        </div>
      </div>

      <div className="md-stats" data-testid="mydeals-stats">
        <div><span>Shared deals</span><b>{deals.length}</b></div>
        <div><span>Fundraising teams</span><b>{raiseCount}</b></div>
        <div><span>Full-access deals</span><b>{grants.filter((g) => g.all).length}</b></div>
        <div><span>Combined EBITDA</span><b>{m.fmt$(deals.reduce((s, d) => s + ((d.model || {}).sde || 0), 0))}</b></div>
      </div>

      {busy && <div className="cm-empty">Fetching your deals…</div>}
      {!busy && deals.length === 0 && (
        <div className="md-empty" data-testid="mydeals-empty"><Lock size={18} /><b>Nothing shared yet</b><span>Ask a partner to assign you to a deal or a fundraising team — it will show up here instantly.</span></div>
      )}
      <div className={`md-grid ${view}`}>
        {rows.map((d) => (
          <DealCard key={d.id} d={d} g={grants.filter((g) => matches(g, d))} pages={pages} team={teams[d.id] || []} view={view}
            current={grantsFor(user, M, dealName).some((g) => matches(g, d)) && (d.name === (dealName || M.dealName))} onOpen={open} />
        ))}
      </div>
    </div>
  );
}
